import {
  VStack,
  Text,
  Button,
  HStack,
  IconButton,
  Spinner,
  Card,
  CardBody,
  Badge,
  Divider,
} from '@chakra-ui/react'
import { useRef, useState } from 'react'
import { MdCloudUpload, MdClose, MdFolder, MdInsertDriveFile } from 'react-icons/md'

interface DicomUploadZoneProps {
  onFilesUploaded: (files: File[]) => void
  uploadedFiles: File[]
  analyzing: boolean
}

export default function DicomUploadZone({
  onFilesUploaded,
  uploadedFiles,
  analyzing,
}: DicomUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return

    const validFiles = Array.from(files).filter((file) => {
      const ext = file.name.toLowerCase()
      return (
        ext.endsWith('.dcm') ||
        ext.endsWith('.dicom') ||
        ext.endsWith('.zip') ||
        ext.endsWith('.tar') ||
        ext.endsWith('.nii') ||
        ext.endsWith('.nii.gz') ||
        ext.endsWith('.mhd') ||
        ext.endsWith('.raw')
      )
    })

    if (validFiles.length > 0) {
      onFilesUploaded([...uploadedFiles, ...validFiles])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const handleRemoveFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index)
    onFilesUploaded(newFiles)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const getFileIcon = (filename: string) => {
    const lower = filename.toLowerCase()
    if (lower.endsWith('.zip') || lower.endsWith('.tar')) {
      return MdFolder
    }
    return MdInsertDriveFile
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Upload Zone */}
      {uploadedFiles.length === 0 ? (
        <Card
          borderWidth={2}
          borderStyle="dashed"
          borderColor={isDragging ? 'blue.500' : 'gray.300'}
          bg={isDragging ? 'blue.50' : 'gray.50'}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          cursor="pointer"
          onClick={() => fileInputRef.current?.click()}
          transition="all 0.2s"
          _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
        >
          <CardBody>
            <VStack spacing={4} py={8}>
              <MdCloudUpload style={{ fontSize: '64px', color: 'var(--chakra-colors-blue-500)' }} />
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  Drop DICOM files here or click to upload
                </Text>
                <Text fontSize="sm" color="gray.600" textAlign="center">
                  Supports: .dcm, .dicom, .zip, .tar, .nii, .nii.gz, .mhd, .raw
                </Text>
              </VStack>
              <Button colorScheme="blue" size="lg">
                Select Files
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <VStack spacing={4} align="stretch">
          {/* Files List */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={3} divider={<Divider />}>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">Uploaded Files</Text>
                  <Badge colorScheme="blue">{uploadedFiles.length} files</Badge>
                </HStack>

                {uploadedFiles.map((file, index) => {
                  const FileIcon = getFileIcon(file.name)
                  return (
                    <HStack key={index} justify="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                      <HStack flex={1} spacing={3}>
                        <FileIcon style={{ fontSize: '24px', color: 'var(--chakra-colors-blue-500)' }} />
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                            {file.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatFileSize(file.size)}
                          </Text>
                        </VStack>
                      </HStack>
                      <IconButton
                        aria-label="Remove file"
                        icon={<MdClose />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleRemoveFile(index)}
                        isDisabled={analyzing}
                      />
                    </HStack>
                  )
                })}
              </VStack>
            </CardBody>
          </Card>

          {/* Add More Button */}
          <Button
            leftIcon={<MdFolder />}
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            colorScheme="blue"
            isDisabled={analyzing}
          >
            Add More Files
          </Button>

          {/* Analysis Status */}
          {analyzing && (
            <HStack
              p={4}
              bg="blue.50"
              borderRadius="md"
              justify="center"
              spacing={3}
            >
              <Spinner color="blue.500" />
              <Text color="blue.700" fontWeight="semibold">
                Analyzing DICOM series with AI...
              </Text>
            </HStack>
          )}
        </VStack>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".dcm,.dicom,.zip,.tar,.nii,.nii.gz,.mhd,.raw"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />
    </VStack>
  )
}
